import { API } from '../../Auth';

const DISCOVER_RUNS_API = 'discoverRuns';
const DATA_SOURCES_BASE = '/api/v1/data-sources';

const normalizeLocation = (loc) =>
  loc && typeof loc === 'object'
    ? {
        cloud: loc.cloud || null,
        region: loc.region || null,
        storageType: loc.storageType || null,
        path: loc.path || null,
        host: loc.host || null,
      }
    : null;

const normalizeClusterMember = (member) => ({
  dataSourceId: member?.dataSourceId != null ? member.dataSourceId : null,
  name: member?.name || '',
  database: member?.database || '',
  engine: member?.engine || '',
  datasourceType: member?.datasourceType || 'RELATIONAL',
  similarityToRepresentative:
    member?.similarityToRepresentative != null ? Number(member.similarityToRepresentative) : null,
  tableCount: member?.tableCount != null ? Number(member.tableCount) : null,
  rowCount: member?.rowCount != null ? Number(member.rowCount) : null,
  // Latest-snapshot size, merged server-side from the worker's member entry — free to
  // display, no extra fetch. Null when the member hasn't been profiled.
  totalSizeBytes: member?.totalSizeBytes != null ? Number(member.totalSizeBytes) : null,
  readOnly: Boolean(member?.readOnly),
  location: normalizeLocation(member?.location),
});

/**
 * GET /api/v1/data-sources/{id}/identity-cluster — the ONE identity cluster containing
 * this source, each member carrying similarityToRepresentative (match %) and a derived
 * `location` block {cloud, region, storageType, path, host} — where the shadow data lives.
 * Returns a singleton cluster when the source isn't clustered/profiled yet.
 */
async function getIdentityCluster(dataSourceId, minSimilarity) {
  const path = `${DATA_SOURCES_BASE}/${encodeURIComponent(dataSourceId)}/identity-cluster`;
  const opts = {};
  if (minSimilarity != null) {
    opts.queryStringParameters = { minSimilarity };
  }
  const response = await API.get(DISCOVER_RUNS_API, path, opts);
  const payload = response?.data ? response.data : response;
  if (!payload || typeof payload !== 'object') return null;
  // The wire shape splits member data: `dataSources[]` carries the enrichment
  // (name, engine, location) while `dataSourceMembers[]` carries the match %.
  // Join the two on dataSourceId.
  const similarityById = {};
  (Array.isArray(payload.dataSourceMembers) ? payload.dataSourceMembers : []).forEach((m) => {
    if (m?.dataSourceId != null && m.similarityToRepresentative != null) {
      similarityById[String(m.dataSourceId)] = Number(m.similarityToRepresentative);
    }
  });
  const members = (Array.isArray(payload.dataSources) ? payload.dataSources : [])
    .map((m) =>
      normalizeClusterMember({
        ...m,
        similarityToRepresentative:
          m.similarityToRepresentative != null
            ? m.similarityToRepresentative
            : similarityById[String(m.dataSourceId)],
      })
    )
    .filter((m) => m.dataSourceId != null);
  return {
    id: payload.id != null ? payload.id : null,
    displayName: payload.displayName || payload.name || '',
    totalRowCount: payload.totalRowCount != null ? Number(payload.totalRowCount) : null,
    dataSources: members,
  };
}

// The mockup's activity-series palette: each job family keeps one color everywhere
// (chart bars, legend chips, table dots). Errors override to danger red.
const ACTIVITY_TYPES = ['DISCOVER', 'PROFILE', 'MASKING', 'ENCRYPT', 'DECRYPT'];

const normalizeActivityEvent = (event) => ({
  id: event?.id != null ? event.id : null,
  jobType: (event?.jobType || 'UNKNOWN').toUpperCase(),
  status: (event?.status || 'UNKNOWN').toUpperCase(),
  startedAt: event?.startedAt || null,
  finishedAt: event?.finishedAt || null,
  errorCode: event?.errorCode || null,
  runLabel: event?.runLabel || null,
});

/**
 * GET /api/v1/data-sources/{id}/activity — unified, paged job history across every job
 * family (DISCOVER, PROFILE, MASKING, ENCRYPT, DECRYPT). Filter with `types`
 * (repeated `type=` params). Returns { content, totalElements, page, size } with a bare
 * array tolerated (older page shapes).
 */
async function getDataSourceActivity(dataSourceId, { types, page = 0, size = 200 } = {}) {
  const path = `${DATA_SOURCES_BASE}/${encodeURIComponent(dataSourceId)}/activity`;
  const queryStringParameters = { page, size };
  if (Array.isArray(types) && types.length > 0) {
    queryStringParameters.type = types;
  }
  const response = await API.get(DISCOVER_RUNS_API, path, { queryStringParameters });
  const payload = response?.data ? response.data : response;
  const content = Array.isArray(payload) ? payload : payload?.content;
  return {
    content: Array.isArray(content) ? content.map(normalizeActivityEvent) : [],
    totalElements:
      payload?.totalElements != null ? Number(payload.totalElements) : Array.isArray(content) ? content.length : 0,
  };
}

export { getIdentityCluster, getDataSourceActivity, ACTIVITY_TYPES };
